import { ScaleElement } from '@scale/base-element';

class ScaleTabs extends ScaleElement {
  #buttons = [];
  #panels = [];
  #activeIndex = 0;

  setup() {
    const nav = this.ref('tablist');
    const tabs = this.querySelectorAll('[data-ref="tab"]');
    if (!nav || !tabs.length) return;

    // Extract buttons from each tab's template and move to nav
    for (const tab of tabs) {
      const template = tab.querySelector('[data-ref="tab-button-template"]');
      const panel = tab.querySelector('[role="tabpanel"]');
      if (!template || !panel) continue;

      const button = template.content.firstElementChild.cloneNode(true);
      button.setAttribute('aria-controls', panel.id || `tabpanel-${crypto.randomUUID().slice(0, 8)}`);
      if (!panel.id) panel.id = button.getAttribute('aria-controls');
      button.id = `tab-${panel.id}`;
      panel.setAttribute('aria-labelledby', button.id);

      nav.appendChild(button);
      this.#buttons.push(button);
      this.#panels.push(panel);

      this.on('click', () => this.#selectTab(button, { focus: true }), { target: button });
      this.on('keydown', (e) => this.#handleKeydown(e), { target: button });

      // Remove the template now that we've extracted the button
      template.remove();
    }

    // Wire optional prev/next arrows
    const prev = this.ref('prevArrow');
    const next = this.ref('nextArrow');
    if (prev) this.on('click', () => this.#navigate(-1), { target: prev });
    if (next) this.on('click', () => this.#navigate(1), { target: next });

    // Activate first tab (without stealing focus on init)
    if (this.#buttons.length) {
      this.#selectTab(this.#buttons[0], { focus: false });
      this.classList.add('is-initialized');
    }
  }

  #navigate(delta) {
    if (!this.#buttons.length) return;
    const count = this.#buttons.length;
    const next = (this.#activeIndex + delta + count) % count;
    this.#selectTab(this.#buttons[next], { focus: false });
  }

  #selectTab(button, { focus = true } = {}) {
    const index = this.#buttons.indexOf(button);
    if (index === -1) return;

    this.#activeIndex = index;

    for (let i = 0; i < this.#buttons.length; i++) {
      this.#buttons[i].setAttribute('aria-selected', i === index ? 'true' : 'false');
      this.#buttons[i].setAttribute('tabindex', i === index ? '0' : '-1');
      this.#panels[i].setAttribute('aria-hidden', i === index ? 'false' : 'true');
    }

    if (focus) button.focus();
  }

  #handleKeydown(e) {
    const index = this.#buttons.indexOf(e.currentTarget);
    let next;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        next = this.#buttons[(index + 1) % this.#buttons.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        next = this.#buttons[(index - 1 + this.#buttons.length) % this.#buttons.length];
        break;
      case 'Home':
        e.preventDefault();
        next = this.#buttons[0];
        break;
      case 'End':
        e.preventDefault();
        next = this.#buttons[this.#buttons.length - 1];
        break;
      default:
        return;
    }

    if (next) this.#selectTab(next);
  }

  get activeIndex() {
    return this.#activeIndex;
  }

  onBlockSelect(e) {
    for (let i = 0; i < this.#panels.length; i++) {
      if (this.#panels[i].closest('[data-ref="tab"]')?.querySelector(`[id*="${e.detail.blockId}"]`)) {
        this.#selectTab(this.#buttons[i]);
        break;
      }
    }
  }
}

if (!customElements.get('scale-tabs')) {
  customElements.define('scale-tabs', ScaleTabs);
}
